import { useState } from 'react'
import { useApolloClient } from '@apollo/client'
import {
  TubeRuleValidateFieldsQuery,
  TubeRuleValidateFieldsDocument,
} from '@src/queries/ConfiguratorTubeRuleValidation.generated'
import { ListError, useProductInput } from './ProductsConfiguration'
import { joinMessagesIntoOne } from './utils'
import { RuleValidateInput } from '@src/types/graphql-types'
import useFormikHelper from './useFormikHelper'
import { TubeProduct, TubeProperties } from '../../utils/enum'
import { ModificationTypes } from './enum'
import messages from '@src/routes/SemiConfigurators/messages'
import { BiTubeProduct } from '../BiTube/enum'
interface TubeValidationResponse {
  validationAPI: (v: RuleValidateInput, u?: boolean) => Promise<ListError[]>
  loading: boolean
}

const useTubeValidation = (): TubeValidationResponse => {
  const client = useApolloClient()

  const formik = useFormikHelper()
  const [loading, setLoading] = useState<boolean>(false)
  const productInput = useProductInput()

  delete productInput?.participantId
  delete productInput?.countryCode

  const validationAPI = async (
    fieldData: RuleValidateInput,
    allTouched?: boolean
  ): Promise<ListError[]> => {
    try {
      formik.setFieldValue('loading', true, false)
      setLoading(true)
      const { data } = await client.query<TubeRuleValidateFieldsQuery>({
        query: TubeRuleValidateFieldsDocument,
        variables: {
          input: fieldData,
          productInput,
        },
      })

      const nameErrors: string[] = []
      const antisenceNameErrors: string[] = []
      const sequenceErrors: string[] = []
      const antisenceSequenceErrors: string[] = []
      const sequenceWarning: string[] = []
      const fiveModErrors: string[] = []
      const threeModErrors: string[] = []
      const scaleErrors: string[] = []
      const purificationErrors: string[] = []
      const formatErrors: string[] = []
      const concentrationErrors: string[] = []
      const tubesErrors: string[] = []
      const purificationWarning: string[] = []
      const amountErrors: string[] = []
      const modificationErrors: string[] = []
      const customerOverhangErrors: string[] = []
      const customerAntisenseOverHangErrors: string[] = []
      const antisenceFiveModErrors: string[] = []
      const antisenceThreeModErrors: string[] = []

      if (data?.getTubeRuleValidateFields?.listErrors) {
        data?.getTubeRuleValidateFields?.listErrors.forEach((error) => {
          if (error?.key === 'name' && error?.value) {
            nameErrors.push(error?.value as string)
          } else if (
            error?.key === 'antisenceName' &&
            error?.value &&
            (formik.touched?.antisenceName ||
              formik.allValues?.hasTriedNextStep ||
              allTouched)
          ) {
            antisenceNameErrors.push(error?.value as string)
          } else if (
            error?.key === 'sequence' &&
            error?.value &&
            (formik.touched?.sequence ||
              formik.allValues?.hasTriedNextStep ||
              allTouched)
          ) {
            sequenceErrors.push(error?.value as string)
          } else if (error?.key === 'fivemod' && error?.value) {
            fiveModErrors.push(error?.value as string)
          } else if (
            (error?.key === 'threemod' && error?.value) ||
            (error?.key === 'fiveModThreeMod' && error?.value)
          ) {
            threeModErrors.push(error?.value as string)
          } else if (error?.key === 'scale' && error?.value) {
            scaleErrors.push(error?.value as string)
          } else if (error?.key === 'amount' && error?.value) {
            amountErrors.push(error?.value as string)
          } else if (error?.key === 'purification' && error?.value) {
            purificationErrors.push(error?.value as string)
          } else if (error?.key === 'format' && error?.value) {
            formatErrors.push(error?.value as string)
          } else if (error?.key === 'concentration' && error?.value) {
            concentrationErrors.push(error?.value as string)
          } else if (error?.key === 'tube' && error?.value) {
            tubesErrors.push(error?.value as string)
          } else if (error?.key === 'modification' && error?.value) {
            modificationErrors.push(error?.value as string)
          } else if (
            error?.key === 'antisencesequence' &&
            error?.value &&
            (formik.touched?.antisenceSequence ||
              formik.allValues?.hasTriedNextStep ||
              allTouched)
          ) {
            antisenceSequenceErrors.push(error?.value as string)
          } else if (error?.key === 'customeroverhang' && error?.value) {
            customerOverhangErrors.push(error?.value as string)
          } else if (
            error?.key === 'customerantisenseoverHang' &&
            error?.value
          ) {
            customerAntisenseOverHangErrors.push(error?.value as string)
          } else if (error?.key === 'antisensfivemod' && error?.value) {
            antisenceFiveModErrors.push(error?.value as string)
          } else if (error?.key === 'antisensthreemod' && error?.value) {
            antisenceThreeModErrors.push(error?.value as string)
          }
        })
        if (data.getTubeRuleValidateFields.listErrors.length === 0) {
          formik.setOligoFieldValue('displayPageError', false)
        }

        formik.setFieldError('name', joinMessagesIntoOne(nameErrors))
        formik.setFieldError(
          'antisenceName',
          joinMessagesIntoOne(antisenceNameErrors)
        )
        formik.setFieldError('sequence', joinMessagesIntoOne(sequenceErrors))
        formik.setFieldError(
          'antisenceSequence',
          joinMessagesIntoOne(antisenceSequenceErrors)
        )
        formik.setFieldError(
          ModificationTypes.FiveMod,
          joinMessagesIntoOne(fiveModErrors)
        )
        formik.setFieldError(
          ModificationTypes.ThreeMod,
          joinMessagesIntoOne(threeModErrors)
        )
        formik.setFieldError('scale', joinMessagesIntoOne(scaleErrors))
        formik.setFieldError('amount', joinMessagesIntoOne(amountErrors))
        formik.setFieldError(
          'purification',
          joinMessagesIntoOne(purificationErrors)
        )
        formik.setFieldError('format', joinMessagesIntoOne(formatErrors))
        formik.setFieldError(
          'concentration',
          joinMessagesIntoOne(concentrationErrors)
        )
        formik.setFieldError('tubes', joinMessagesIntoOne(tubesErrors))
        formik.setOligoFieldValue(
          'listErrors',
          data?.getTubeRuleValidateFields?.listErrors.filter(
            (error) => error.key !== 'ReviewError'
          ) as any, // TODO we need to fix this type. Sometimes an error is a string, number, or object and an array of any one of these
          false
        )
        formik.setFieldError(
          'modification',
          joinMessagesIntoOne(modificationErrors)
        )
        formik.setFieldError(
          'customerOverhang',
          joinMessagesIntoOne(customerOverhangErrors)
        )
        formik.setFieldError(
          'customerAntisenseOverHang',
          joinMessagesIntoOne(customerAntisenseOverHangErrors)
        )
        formik.setFieldError(
          'antisenceThreeMod',
          joinMessagesIntoOne(antisenceThreeModErrors)
        )
        formik.setFieldError(
          'antisenceFiveMod',
          joinMessagesIntoOne(antisenceFiveModErrors)
        )
      }

      if (data?.getTubeRuleValidateFields?.listWarnings) {
        data?.getTubeRuleValidateFields?.listWarnings.forEach((error) => {
          if (error?.key === 'sequence' && error?.value) {
            sequenceWarning.push(error?.value as string)
          }
          if (error?.key === 'purification' && error?.value) {
            purificationWarning.push(error?.value as string)
          }
        })
        formik.setOligoFieldValue('sequenceWarning', sequenceWarning, false)
        formik.setOligoFieldValue(
          'purificationWarning',
          purificationWarning,
          false
        )
      }

      if (data?.getTubeRuleValidateFields?.listScale) {
        const scaleData: string[] = data?.getTubeRuleValidateFields.listScale
        formik.setOligoFieldValue('enabledScaleData', scaleData, false)
      }

      if (data?.getTubeRuleValidateFields?.listAmount) {
        const amountData: string[] = data?.getTubeRuleValidateFields.listAmount
        formik.setOligoFieldValue('enabledAmountData', amountData, false)
      }

      if (data?.getTubeRuleValidateFields?.listConcentration) {
        const concentrationData: string[] =
          data?.getTubeRuleValidateFields.listConcentration.length === 1 &&
          data?.getTubeRuleValidateFields.listConcentration[0] === 'None'
            ? ['']
            : data?.getTubeRuleValidateFields.listConcentration
        formik.setOligoFieldValue(
          'enabledConcentrationData',
          concentrationData,
          false
        )
        // For FastDuplex Set concentration based on BE response
        const { FastDuplex } = BiTubeProduct
        const products: string[] = [FastDuplex]
        if (
          productInput.productType &&
          products.includes(productInput.productType) &&
          formik.selectedOligoValues?.amount !== ''
        ) {
          formik.setOligoFieldValue(
            TubeProperties.Concentration,
            concentrationData[0],
            false
          )
          formik.setFieldError('concentration', joinMessagesIntoOne([]))
        }
      }

      if (data?.getTubeRuleValidateFields?.listAmount) {
        const { EasyOligo, PureSimple, LongOligo } = TubeProduct
        const products: string[] = [EasyOligo, PureSimple, LongOligo]
        if (
          productInput.productType &&
          products.includes(productInput.productType)
        ) {
          const amountData: string[] =
            data?.getTubeRuleValidateFields.listAmount.length === 1 &&
            data?.getTubeRuleValidateFields.listAmount[0] === 'None'
              ? ['']
              : data?.getTubeRuleValidateFields.listAmount
          formik.setOligoFieldValue(TubeProperties.Amount, amountData[0], false)
        }
      }

      if (data?.getTubeRuleValidateFields?.listPurification) {
        const purificationData: string[] =
          data?.getTubeRuleValidateFields.listPurification
        formik.setOligoFieldValue(
          'enabledPurificationData',
          purificationData,
          false
        )
      }

      if (data?.getTubeRuleValidateFields?.listFormat) {
        const formatData: string[] = data?.getTubeRuleValidateFields.listFormat
        formik.setOligoFieldValue('enabledFormatData', formatData, false)
        const { EasyOligo } = TubeProduct
        const products: string[] = [EasyOligo]
        if (
          productInput.productType &&
          products.includes(productInput.productType)
        ) {
          formik.setOligoFieldValue(TubeProperties.Format, formatData[0], false)
        }
      }

      // For miRNA Sequence is not an editable field and is derived from the Antisense sequence
      if (
        data?.getTubeRuleValidateFields?.sequence &&
        productInput.productType === TubeProduct.mirna
      ) {
        formik.setOligoFieldValue(
          TubeProperties.Sequence,
          data?.getTubeRuleValidateFields?.sequence
        )
      }

      if (data?.getTubeRuleValidateFields?.listFiveMod) {
        const fiveModData: string[] =
          data?.getTubeRuleValidateFields.listFiveMod
        formik.setOligoFieldValue(
          'enabledFiveModifications',
          fiveModData,
          false
        )
      }
      if (data?.getTubeRuleValidateFields?.listThreeMod) {
        const threeModData: string[] =
          data?.getTubeRuleValidateFields.listThreeMod
        formik.setOligoFieldValue(
          'enabledThreeModifications',
          threeModData,
          false
        )
      }
      if (data?.getTubeRuleValidateFields?.listAdditionalServices) {
        const additionalServiceData: string[] =
          data?.getTubeRuleValidateFields.listAdditionalServices
        formik.setOligoFieldValue(
          'enabledAdditionalServices',
          additionalServiceData,
          false
        )
      }
      if (data?.getTubeRuleValidateFields?.listInternalMod) {
        const internalModData: string[] =
          data?.getTubeRuleValidateFields.listInternalMod
        formik.setOligoFieldValue(
          'enabledInternalModifications',
          internalModData,
          false
        )
      }
      formik.setOligoFieldValue(
        'isLNABaseEnable',
        !!data.getTubeRuleValidateFields?.isLNABaseEnable
      )

      data?.getTubeRuleValidateFields?.baseCount &&
        formik.setOligoFieldValue(
          'baseCount',
          `${data.getTubeRuleValidateFields.baseCount}`,
          false
        )
      data?.getTubeRuleValidateFields?.molecularWeight &&
        formik.setOligoFieldValue(
          'molecularWeight',
          data.getTubeRuleValidateFields.molecularWeight,
          false
        )
      data?.getTubeRuleValidateFields?.secondaryStructure &&
        formik.setOligoFieldValue(
          'secondaryStructure',
          messages[data.getTubeRuleValidateFields.secondaryStructure]
            .defaultMessage || 'None',
          false
        )
      data?.getTubeRuleValidateFields?.primerDimer &&
        formik.setOligoFieldValue(
          'primerDimer',
          data.getTubeRuleValidateFields.primerDimer,
          false
        )
      data?.getTubeRuleValidateFields?.meltingTemperature &&
        formik.setOligoFieldValue(
          'meltingTemperature',
          data.getTubeRuleValidateFields.meltingTemperature,
          false
        )
      data?.getTubeRuleValidateFields?.gcContent &&
        formik.setOligoFieldValue(
          'gcContent',
          data.getTubeRuleValidateFields.gcContent,
          false
        )
      data?.getTubeRuleValidateFields?.listTubes &&
        formik.setOligoFieldValue(
          'listNumberOfTubes',
          data.getTubeRuleValidateFields.listTubes.map((i) => Number(i)),
          false
        )
      data?.getTubeRuleValidateFields?.listMinimumYield &&
        formik.setOligoFieldValue(
          'listMinimumYield',
          data.getTubeRuleValidateFields.listMinimumYield,
          false
        )
      data?.getTubeRuleValidateFields?.antisenceName &&
        formik.setOligoFieldValue(
          'antisenceName',
          data.getTubeRuleValidateFields.antisenceName,
          false
        )
      data?.getTubeRuleValidateFields?.antisenseBaseCount &&
        formik.setOligoFieldValue(
          'antisenseBaseCount',
          data.getTubeRuleValidateFields.antisenseBaseCount,
          false
        )

      data?.getTubeRuleValidateFields?.antisenseMolecularWeight &&
        formik.setOligoFieldValue(
          'antisenseMolecularWeight',
          data.getTubeRuleValidateFields.antisenseMolecularWeight,
          false
        )

      data?.getTubeRuleValidateFields?.antisensePrimerDimer &&
        formik.setOligoFieldValue(
          'antisensePrimerDimer',
          data.getTubeRuleValidateFields.antisensePrimerDimer,
          false
        )

      data?.getTubeRuleValidateFields?.antisenseMeltingTemperature &&
        formik.setOligoFieldValue(
          'antisenseMeltingTemperature',
          data.getTubeRuleValidateFields.antisenseMeltingTemperature,
          false
        )

      data?.getTubeRuleValidateFields?.antisenseGcContent &&
        formik.setOligoFieldValue(
          'antisenseGcContent',
          data.getTubeRuleValidateFields.antisenseGcContent,
          false
        )

      data?.getTubeRuleValidateFields?.antisenseSecondaryStructure &&
        formik.setOligoFieldValue(
          'antisenseSecondaryStructure',
          data.getTubeRuleValidateFields.antisenseSecondaryStructure,
          false
        )
      setLoading(false)
      formik.setFieldValue('loading', false, false)
      return data?.getTubeRuleValidateFields?.listErrors || []
    } catch (err) {
      formik.setFieldValue('loading', false, false)

      setLoading(false)
      return []
    }
  }

  return {
    validationAPI: validationAPI,
    loading: loading,
  }
}

export default useTubeValidation
